/**
 * Structure-level add-ons: NPS (employer benefit / 80CCD(2)),
 * VPF (employee deduction / 80C), Meal Voucher (₹200/meal from Apr 2026).
 * Pattern mirrors Performance Bonus: structure enables → employee opts in.
 */

export const MEAL_VOUCHER_MAX_PER_MEAL = 200; // Income-Tax Rules 2026
export const MEAL_VOUCHER_DEFAULT_MEALS_PER_DAY = 2;
export const VPF_MAX_PERCENT = 88; // with statutory PF 12% ⇒ 100% of PF wage
export const EMPLOYER_RETIREMENT_PERQ_CAP = 750000; // §17(2)(vii) employer PF+NPS+SA
export const SECTION_80C_CAP = 150000;

export const NPS_RATE_MODES = [
  { value: "flat", label: "Flat % (structure default)" },
  { value: "employee", label: "Employee level %" },
];

export const VPF_RATE_MODES = [
  { value: "flat_percent", label: "Flat %" },
  { value: "flat_amount", label: "Flat amount (₹)" },
  { value: "employee", label: "Employee level" },
];

export const MEAL_RATE_MODES = [
  { value: "flat", label: "Flat monthly amount" },
  { value: "employee", label: "Employee level amount" },
];

export function defaultNpsConfig() {
  return {
    enabled: false,
    rateMode: "flat",
    flatPercent: 10,
    wageBasis: "basic", // basic | pf_wage
    effectiveFrom: "",
  };
}

export function defaultVpfConfig() {
  return {
    enabled: false,
    rateMode: "flat_percent",
    flatPercent: 0,
    flatAmount: 0,
    effectiveFrom: "",
  };
}

export function defaultMealVoucherConfig() {
  return {
    enabled: false,
    rateMode: "flat",
    amountPerMeal: MEAL_VOUCHER_MAX_PER_MEAL,
    mealsPerDay: MEAL_VOUCHER_DEFAULT_MEALS_PER_DAY,
    flatMonthlyAmount: 0,
    effectiveFrom: "",
  };
}

function pickEffectiveFrom(raw) {
  const from =
    raw?.effectiveFrom ||
    raw?.statusEffectiveFrom ||
    (raw?.effectiveMonth ? `${String(raw.effectiveMonth).slice(0, 7)}-01` : "");
  const s = String(from || "").slice(0, 10);
  return /^\d{4}-\d{2}/.test(s) ? (s.length >= 10 ? s.slice(0, 10) : `${s.slice(0, 7)}-01`) : "";
}

export function normalizeNpsConfig(raw) {
  const d = defaultNpsConfig();
  if (!raw || typeof raw !== "object") return d;
  const rateMode = String(raw.rateMode || d.rateMode).toLowerCase() === "employee" ? "employee" : "flat";
  const pct = Number(raw.flatPercent);
  return {
    enabled: !!raw.enabled,
    rateMode,
    flatPercent: Number.isFinite(pct) && pct >= 0 ? Math.min(pct, 14) : d.flatPercent,
    wageBasis: String(raw.wageBasis || d.wageBasis).toLowerCase() === "pf_wage" ? "pf_wage" : "basic",
    effectiveFrom: pickEffectiveFrom(raw) || d.effectiveFrom,
  };
}

export function normalizeVpfConfig(raw) {
  const d = defaultVpfConfig();
  if (!raw || typeof raw !== "object") return d;
  const mode = String(raw.rateMode || d.rateMode).toLowerCase();
  const rateMode = ["flat_percent", "flat_amount", "employee"].includes(mode) ? mode : "flat_percent";
  const pct = Number(raw.flatPercent);
  const amt = Number(raw.flatAmount);
  return {
    enabled: !!raw.enabled,
    rateMode,
    flatPercent:
      Number.isFinite(pct) && pct >= 0 ? Math.min(pct, VPF_MAX_PERCENT) : 0,
    flatAmount: Number.isFinite(amt) && amt >= 0 ? amt : 0,
    effectiveFrom: pickEffectiveFrom(raw) || d.effectiveFrom,
  };
}

export function normalizeMealVoucherConfig(raw) {
  const d = defaultMealVoucherConfig();
  if (!raw || typeof raw !== "object") return d;
  const rateMode = String(raw.rateMode || d.rateMode).toLowerCase() === "employee" ? "employee" : "flat";
  let perMeal = Number(raw.amountPerMeal);
  if (!Number.isFinite(perMeal) || perMeal < 0) perMeal = MEAL_VOUCHER_MAX_PER_MEAL;
  perMeal = Math.min(perMeal, MEAL_VOUCHER_MAX_PER_MEAL);
  let meals = Number(raw.mealsPerDay);
  if (!Number.isFinite(meals) || meals < 1) meals = MEAL_VOUCHER_DEFAULT_MEALS_PER_DAY;
  meals = Math.min(Math.max(Math.round(meals), 1), 3);
  const flat = Number(raw.flatMonthlyAmount);
  return {
    enabled: !!raw.enabled,
    rateMode,
    amountPerMeal: perMeal,
    mealsPerDay: meals,
    flatMonthlyAmount: Number.isFinite(flat) && flat >= 0 ? flat : 0,
    effectiveFrom: pickEffectiveFrom(raw) || d.effectiveFrom,
  };
}

export function normalizePerformanceBonusConfig(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    enabled: !!r.enabled,
    frequency: r.frequency || "annual",
    effectiveFrom: pickEffectiveFrom(r) || "",
  };
}

/** Soft monthly exempt hint: ₹200 × meals/day × ~22 working days */
export function mealVoucherMonthlyCap(config) {
  const c = normalizeMealVoucherConfig(config);
  return Math.round(c.amountPerMeal * c.mealsPerDay * 22);
}

export function validateVpfEmployeeValues({ percent, amount, rateMode, pfWageMonthly = 0 }) {
  const mode = String(rateMode || "flat_percent");
  if (mode === "flat_amount" || (amount != null && amount !== "" && mode === "employee")) {
    const a = Number(amount) || 0;
    if (a < 0) return "VPF amount cannot be negative";
    if (pfWageMonthly > 0 && a > pfWageMonthly * (VPF_MAX_PERCENT / 100) + 0.01) {
      return `VPF amount cannot exceed ${VPF_MAX_PERCENT}% of PF wage (₹${Math.round(pfWageMonthly * VPF_MAX_PERCENT / 100).toLocaleString("en-IN")})`;
    }
    return null;
  }
  const p = Number(percent) || 0;
  if (p < 0) return "VPF % cannot be negative";
  if (p > VPF_MAX_PERCENT) {
    return `VPF % cannot exceed ${VPF_MAX_PERCENT}% (PF 12% + VPF ≤ 100% of PF wage)`;
  }
  return null;
}

export function validateMealVoucherMonthly(monthlyAmount, config) {
  const cap = mealVoucherMonthlyCap(config);
  const a = Number(monthlyAmount) || 0;
  if (a < 0) return "Meal voucher amount cannot be negative";
  if (a > cap + 1) {
    return `Meal voucher monthly amount exceeds tax-free hint ₹${cap.toLocaleString("en-IN")} (₹${MEAL_VOUCHER_MAX_PER_MEAL}/meal × ${normalizeMealVoucherConfig(config).mealsPerDay} meals × ~22 days). Excess may be taxable.`;
  }
  return null;
}

export function resolveEmployeeNpsPercent(structureNps, employeeNps) {
  const s = normalizeNpsConfig(structureNps);
  if (!s.enabled || !employeeNps?.enabled) return 0;
  if (s.rateMode === "flat") return Number(s.flatPercent) || 0;
  const p = Number(employeeNps.percent);
  return Number.isFinite(p) && p >= 0 ? Math.min(p, 14) : 0;
}

export function resolveEmployeeVpfMonthly(structureVpf, employeeVpf, pfWageMonthly) {
  const s = normalizeVpfConfig(structureVpf);
  if (!s.enabled || !employeeVpf?.enabled) return 0;
  const wage = Math.max(0, Number(pfWageMonthly) || 0);
  const mode = s.rateMode === "employee" ? String(employeeVpf.rateMode || "flat_percent") : s.rateMode;

  if (mode === "flat_amount") {
    const amt = s.rateMode === "employee" ? Number(employeeVpf.amount) || 0 : Number(s.flatAmount) || 0;
    const maxAmt = wage * (VPF_MAX_PERCENT / 100);
    return Math.min(Math.max(0, amt), maxAmt || amt);
  }
  const pct =
    s.rateMode === "employee"
      ? Number(employeeVpf.percent) || 0
      : Number(s.flatPercent) || 0;
  const capped = Math.min(Math.max(0, pct), VPF_MAX_PERCENT);
  return Math.round((wage * capped) / 100 * 100) / 100;
}

export function resolveEmployeeMealMonthly(structureMeal, employeeMeal, attendance = null) {
  const s = normalizeMealVoucherConfig(structureMeal);
  if (!s.enabled || !employeeMeal?.enabled) return 0;
  if (s.rateMode === "flat") return Number(s.flatMonthlyAmount) || 0;
  const direct = Number(
    employeeMeal.monthlyAmount ?? employeeMeal.flatMonthlyAmount ?? employeeMeal.amount
  );
  if (Number.isFinite(direct) && direct > 0) return direct;
  const perMeal = Math.min(
    Math.max(0, Number(employeeMeal.amountPerMeal ?? s.amountPerMeal) || 0),
    MEAL_VOUCHER_MAX_PER_MEAL
  );
  const meals = Math.min(
    Math.max(1, Math.round(Number(employeeMeal.mealsPerDay ?? s.mealsPerDay) || 2)),
    3
  );
  if (perMeal <= 0) return 0;
  const cal = Math.max(1, Number(attendance?.calendarDays) || 22);
  return Math.round(perMeal * meals * cal * 100) / 100;
}

export const STATUTORY_FLAG_KEYS = [
  { key: "pfApplicable", label: "PF Applicable", defaultOn: true },
  { key: "ceilingPf", label: "Ceiling PF", defaultOn: true },
  { key: "epsApplicable", label: "EPS", defaultOn: false },
  { key: "esiApplicable", label: "ESI Applicable", defaultOn: false },
  { key: "ptApplicable", label: "PT Applicable", defaultOn: true },
  { key: "gratuityApplicable", label: "Gratuity Applicable", defaultOn: true },
];
